import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { OkObject } from '@it2go/types';
import { CalendarItemCreateInput } from '@libs/types/src/services/input-type/calendar/calendar-item.create-input';
import { CalendarItemDeleteInput } from '@libs/types/src/services/input-type/calendar/calendar-item.delete-input';
import { CalendarItemGetInput } from '@libs/types/src/services/input-type/calendar/calendar-item.get-input';
import { CalendarItemGetMyInput } from '@libs/types/src/services/input-type/calendar/calendar-item.get-my-input';
import { CalendarItemPatchInput } from '@libs/types/src/services/input-type/calendar/calendar-item.patch-input';
import { CalendarItemMoveWorkerInput } from '@libs/types/src/services/input-type/calendar/calendar-item.move-worker-input';
import {
    CalendarEventOccurrenceGridObject,
    CalendarEventOccurrenceObject,
} from '@libs/types/src/services/object-type/calendar/calendar-item.object';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { filterFrag } from './types/filter.types';

type CalendarItemCreateRequest = Omit<CalendarItemCreateInput, 'trimRRule'>;

@Injectable({ providedIn: 'root' })
export class CalendarItemGqlService extends GqlServiceV2 {

    public getMyCalendarItems(input: CalendarItemGetMyInput, fragment: string): Promise<CalendarEventOccurrenceObject[]> {
        return this.query<CalendarEventOccurrenceObject[]>(`
            query($input: CalendarItemGetMyInput!) {
                calendarItem {
                    getMyItems(input: $input) { ${fragment} }
                }
            }
        `, { input });
    }

    public getCalendarWorkPlan(input: CalendarItemGetInput, fragment: string): Promise<CalendarEventOccurrenceObject[]> {
        return this.query<CalendarEventOccurrenceObject[]>(`
            query($input: CalendarItemGetInput!) {
                calendarItem {
                    workPlan(input: $input) { ${fragment} }
                }
            }
        `, { input });
    }

    public getCalendarItems(input: CalendarItemGetInput, fragment: string): Promise<CalendarEventOccurrenceObject[]> {
        return this.query<CalendarEventOccurrenceObject[]>(
            `
                query($input: CalendarItemGetInput!) {
                    calendarItem {
                        get(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public filterCalendarItems(input: GridInput, fragment: string): Promise<CalendarEventOccurrenceGridObject> {
        return this.query<CalendarEventOccurrenceGridObject>(
            `
                query($input: GridInput!) {
                    calendarItem {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createCalendarItem(input: CalendarItemCreateRequest): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: CalendarItemCreateInput!) {
                    calendarItem {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public patchCalendarItem(input: CalendarItemPatchInput): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: CalendarItemPatchInput!) {
                    calendarItem {
                        patch(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public moveCalendarItemWorker(input: CalendarItemMoveWorkerInput): Promise<OkObject> {
        return this.mutate<OkObject>(
            `mutation($input: CalendarItemMoveWorkerInput!) {
                calendarItem {
                    moveWorker(input: $input) { ok }
                }
            }`,
            { input },
        );
    }

    public deleteCalendarItem(input: CalendarItemDeleteInput): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: CalendarItemDeleteInput!) {
                    calendarItem {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }
}
